<template>
  <base-modal name="addAttendee" width="960">
    <template #body>
      <div class="modal-body overflow-y-hidden p-20">
        <div class="flex justify-end mb-20">
          <close-icon
            class="mt-20 mb-10 cursor-pointer"
            @click.native="$store.dispatch('modal/close', 'addAttendee')"
          />
        </div>
        <div class="flex gap-20 sm:flex-col overflow-y-auto h-600 sm:px-10">
          <div class="w-1/2 sm:w-full">
            <div class="modal-title">
              <h3 class="modal-content-title text-18 font-bold" data-cy="attendee-modal-title">
                {{ $t('add_attendee') }}
              </h3>
            </div>
            <div class="my-16">
              <base-simple-search-box
                id="cm_s_attendee"
                width="full"
                height="40"
                type="attendee"
                @searchedText="getSearchedText"
              />
            </div>
            <div
              class="h-48 w-full bg-warning05 flex rounded gap-x-10 p-15 items-center mb-20"
              v-if="
                isMoreThanCapacity &&
                capacity !== null &&
                capacity &&
                !need_room
              "
            >
              <warning />
              <p class="text-warning">
                {{ $t('room_capacity_is_person', { roomCapacity: capacity }) }}
              </p>
            </div>
            <div class="flex items-center justify-between mb-12">
              <p class="text-sm text-title">
                {{ attendeesArray.length }} {{ $t('members_selected') }}
              </p>
              <base-skeleton
                v-if="isLoadingMembers"
                name="memberIsLoading"
                width="90"
                height="30"
              />
              <base-button
                v-else-if="!searchedMember"
                @click.native="AttendeesSelectorHandler"
                buttonType="cancel"
                class="w-max px-10"
                height="30"
                radius="sm"
                data-cy="select-all-member"
              >
                <template #text>
                  <p class="text-sm">{{ selectAllButtonText }}</p>
                </template>
              </base-button>
            </div>
            <div class="member-selector">
              <div v-if="isLoadingMembers">
                <div class="single-member" v-for="index in 5" :key="index">
                  <div class="flex items-center">
                    <base-skeleton name="memberIsLoading" width="20" height="20" />
                    <div class="mx-12">
                      <base-skeleton name="memberIsLoading" rounded="full" width="44" height="44" />
                    </div>
                    <div>
                      <base-skeleton
                        class="mb-10"
                        name="memberIsLoading"
                        rounded="full"
                        width="70"
                        height="12"
                      />
                      <base-skeleton name="memberIsLoading" rounded="full" width="70" height="12" />
                    </div>
                  </div>
                </div>
              </div>
              <div v-else-if="!isLoadingMembers && allMembers.length !== 0">
                <div class="single-member" v-for="member in allMembers" :key="member.id">
                  <input
                    class="check-box-classes"
                    type="checkbox"
                    v-model="attendeesArray"
                    :value="member.id"
                    :disabled="isDisabled"
                  />
                  <div class="mx-12">
                    <img
                      v-if="member.avatar != null"
                      :src="resizer(member.avatar)"
                      class="w-44 h-44 rounded-full"
                      alt="member avatar"
                      :class="[imgError ? 'object-scale-down' : 'object-cover']"
                      @error="imageError($event)"
                    />
                    <name-avatar v-else rounded="full" size="44" :text="member.name" />
                  </div>
                  <div>
                    <p class="font-bold">{{ member.name }}</p>
                    <p>{{ member.job_position }}</p>
                  </div>
                </div>
              </div>
              <div v-else>
                <p class="text-center py-48 w-full">
                  {{ $t('no_item_found') }}
                </p>
              </div>
            </div>
            <div class="flex justify-between relative pt-10" v-outside="closeGuestTab">
              <div class="tooltip" v-if="guestTab"></div>
              <div v-if="guestTab" class="guest-section">
                <div class="flex justify-between items-center">
                  <span class="text-title font-bold"> {{ $t('add_guest') }} </span>
                  <close-icon class="w-24 cursor-pointer" @click.native="guestTab = false" />
                </div>
                <div
                  class="flex items-center justify-between gap-x-5 mt-20 sm:flex-col sm:gap-y-20"
                >
                  <phone-number-input
                    @countryCode="getCountryCode"
                    @isPhoneValid="phoneValidation"
                    :isLogin="false"
                    v-model="user.mobile"
                    :userCountryCode="lastGuestCode"
                    radius="sm"
                    :AutoFocus="false"
                    width="224"
                    height="32"
                    CountrySelectorHeight="24"
                    CountrySelectorWidth="224"
                    v-if="renderComponent"
                  />
                  <div class="flex justify-between w-224">
                    <input
                      class="border border-default rounded-sm h-32 w-132 sm:w-150 bg-secondary"
                      :placeholder="$t('guest_name')"
                      v-model="user.name"
                      type="text"
                    />
                    <div
                      class="add-guest"
                      @click="addGuest()"
                      :disabled="!(user.name.length > 0 && user.mobile.length > 0)"
                    >
                      <AddIcon
                        :color="
                          user.name.length > 0 && user.mobile.length > 0 ? '#007AFF' : '#BDBEBF'
                        "
                        width="20"
                        height="20"
                      />
                    </div>
                  </div>
                </div>
                <div class="flex flex-col column-gap mt-20">
                  <div v-for="(user, index) in guests" :key="index">
                    <div
                      class="guests-info"
                    >
                      <span class="text-title w-150 text-center" dir="ltr">
                        {{ user.country_code.replace('00', '+') }}{{ user.mobile }}
                      </span>
                      <div class="flex justify-between sm:justify-center items-center w-full">
                        <span class="text-title w-102 truncate text-center">
                          {{ user.name }}
                        </span>
                        <span
                          class="text-danger w-10 font-bold cursor-pointer"
                          @click="removeGuest(index)"
                        >
                          -
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <base-button
                buttonType="primary"
                width="265"
                height="40"
                radius="default"
                @click.native="submitAttendeesHandler"
                data-cy="save-attendees"
              >
                <template #text
                  >{{ $t('save_attendees') }}({{ selectedMembersCount + guests.length }})
                </template>
              </base-button>
              <div
                class="guests-button"
                @click="openGuestTab()"
              >
                {{ $t('guest') }}({{ guests.length }})
              </div>
            </div>
          </div>
          <div class="attendee-calendar-container">
            <p class="text-subtitle font-semibold text-16 px-5">
              {{ $t('attendees_meetings_plan') }}
            </p>
            <full-event-calender
              :fullCalendarHeight="550"
              :dateToStart="new Date(startTime)"
              :isPinchActive="false"
              :oneHourInPixel="80"
              markOverLappingBorders
              type="day"
              :locale="$i18n.locale === 'fa' ? 'fa-IR' : 'en-US'"
              :rtl="$i18n.locale === 'fa' || $i18n.locale === 'ar'"
              :eventList="computedReservationsList"
              :calendar="calendar === 'Solar' ? 'persian' : 'gregory'"
              :theme="theme"
              hideHeader
            >
            </full-event-calender>
          </div>
        </div>
      </div>
    </template>
  </base-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import PhoneNumberInput from '@/components/PhoneNumberInput.vue';
import AddIcon from '@/assets/icons/vue/add.vue';
import Warning from '@/assets/icons/vue/warning.vue';
import BaseSimpleSearchBox from '@/components/base/BaseSimpleSearchBox.vue';
import NameAvatar from '@/components/base/BaseNameAvatar.vue';
import BaseModal from '@/components/modals/BaseModal.vue';
import CloseIcon from '@/assets/icons/vue/close_normal.vue';
import axiosConfig from '@/services/axios.config';
import serverErrorsHandler from '@/utils/serverErrorsHandler';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseSkeleton from '@/components/base/BaseSkeleton.vue';
import imageResizer from '@/utils/image-resizer';
import i18n from '@/utils/i18n/i18n';

const moment = require('moment-jalaali');

export default {
  name: 'addAttendee',
  components: {
    BaseModal,
    CloseIcon,
    BaseSimpleSearchBox,
    NameAvatar,
    Warning,
    PhoneNumberInput,
    AddIcon,
    BaseButton,
    BaseSkeleton,
  },
  data() {
    return {
      date: new Date().toISOString().split('T')[0],
      imgError: false,
      allMembers: [],
      attendeesArray: [],
      isLoadingMembers: true,
      guests: [],
      guestTab: false,
      searchedMember: '',
      isDisabled: false,
      renderComponent: true,
      user: {
        mobile: '',
        name: '',
        country_code: '',
      },
      lastGuestCode: '',
      usersReservations: [],
    };
  },
  mounted() {
    this.initializeData();
  },
  props: {
    initialMembers: {
      type: Array,
    },
    initialGuests: {
      type: Array,
    },
    roomReservations: {
      type: Array,
      default: () => [],
    },
    reserveMeetingCompanyId: {
      type: Number,
      required: true,
    },
    deviceId: {
      type: Number,
    },
    capacity: {
      type: Number,
    },
    need_room: {
      type: Boolean,
    },
    startTime: {
      type: String,
      required: true,
    },
    endTime: {
      type: String,
      required: true,
    },
    isMoreThanCapacity: {
      type: Boolean,
    },
    calendar: {
      type: String,
    },
    previewEvent: {
      type: Object,
    },
  },
  computed: {
    ...mapGetters({
      userInformation: 'getUserInformation',
      theme: 'getTheme',
    }),
    dependencyWatcher() {
      return `${this.startTime}-${this.endTime}-${this.deviceId}-
      ${this.reserveMeetingCompanyId}-${this.guests}-${this.attendeesArray}`;
    },
    selectAllButtonText() {
      return this.allMembers.length > 0 && this.attendeesArray.length === this.allMembers.length
        ? this.$t('deselect_all')
        : this.$t('select_all');
    },
    selectedMembersCount() {
      return this.attendeesArray.length;
    },
    changeEvents() {
      return this.attendeesArray?.length === 0 && this.guests?.length === 0
      ? this.roomReservations : this.usersReservations;
    },
    computedReservationsList() {
      const arr = [...this.changeEvents];
      arr.forEach((event) => {
        if (event.status !== 'approve' && event.status) {
          // eslint-disable-next-line no-param-reassign
          event.notInjectBackGroundColors = true;
          // eslint-disable-next-line no-param-reassign
          event.hideIsGoing = true;
          // eslint-disable-next-line no-param-reassign
          event.classList = [' event-border'];
          if (event.status === 'pending') {
            // eslint-disable-next-line no-param-reassign
            event.badgeClassList = [' warning-badge'];
            // eslint-disable-next-line no-param-reassign
            event.badge = i18n.t('awaiting_confirmation');
          } else {
            // eslint-disable-next-line no-param-reassign
            event.badgeClassList = [' danger-badge'];
            // eslint-disable-next-line no-param-reassign
            event.badge = i18n.t('rejected');
          }
        }
      });
      if (this.previewEvent) {
        arr.push(this.previewEvent);
      }
      return arr;
    },
    reservationUrl() {
      const guestMobileArr = this.guests.map((guest) => guest.mobile);
      let attendeesArrayQueryString = '';
      this.attendeesArray.forEach((attendee) => {
        attendeesArrayQueryString += `&attendees_user_id%5B%5D=${attendee}`;
      });
      const checkDateSame = moment(this.endTime.slice(0, 10)).isSame(this.startTime.slice(0, 10));
      if (guestMobileArr.length > 0) {
        return `companies/get-plan?company_id=${this.reserveMeetingCompanyId}&started_at=${this.startTime}
        &ended_at=${checkDateSame ? this.endTime : `${this.startTime.slice(0, 10)} 23:59:59`}&guests_mobile%5B%5D=${guestMobileArr}${attendeesArrayQueryString}`;
      }
      return `companies/get-plan?company_id=${this.reserveMeetingCompanyId}&started_at=${this.startTime}
        &ended_at=${checkDateSame ? this.endTime : `${this.startTime.slice(0, 10)} 23:59:59`}${attendeesArrayQueryString}`;
    },
  },
  watch: {
    allMembers() {
      this.$emit('setAllMembers', this.allMembers);
    },
    attendeesArray() {
      this.$emit('setAttendeesArray', this.attendeesArray);
    },
    guests() {
      this.$emit('setGuests', this.guests);
    },
    initialGuests(val) {
      this.guests = val;
    },
    initialMembers(val) {
      this.attendeesArray = val;
    },
    dependencyWatcher() {
      this.getReservations();
    },
  },
  methods: {
    async initializeData() {
    await this.getAllMembersHandler();
    if (this.initialMembers) {
      this.attendeesArray = this.initialMembers;
    } else {
      this.attendeesArray = [this.userInformation.id];
    }
    if (this.initialGuests) {
      this.guests = this.initialGuests;
    }
    this.getReservations();
  },
    getReservations() {
      if (this.attendeesArray.length > 0 || this.guests.length > 0) {
        axiosConfig
        .get(this.reservationUrl)
        .then((response) => {
          this.usersReservations = response.data.data;
        })
        .catch((error) => {
          serverErrorsHandler(error);
          throw error;
        });
      }
    },
    imageError($event) {
      this.imgError = true;
      this.OnImageError($event);
    },
    resizer(image) {
      return imageResizer(image, this.imageSize);
    },
    closeGuestTab() {
      if (this.guestTab === true) {
        this.guestTab = false;
      }
    },
    removeGuest(index) {
      this.guests.splice(index, 1);
    },
    getCountryCode(code) {
      this.user.country_code = code;
    },
    phoneValidation(stage) {
      this.isBtnDisabled = stage;
    },
    openGuestTab() {
      this.guestTab = !this.guestTab;
    },
    addGuest() {
      this.guests.push(JSON.parse(JSON.stringify(this.user)));
      this.user.name = '';
      this.lastGuestCode = this.user.country_code;
      this.renderComponent = false;
      this.$nextTick(() => {
        this.renderComponent = true;
      });
    },
    attendeesHandler() {
      const attendedMembers = [];
      this.allMembers.map((member) => this.attendeesArray.map((el) => {
          if (member.id === el) {
            return attendedMembers.push(member);
          }
          return [];
        }));
    },
    submitAttendeesHandler() {
      this.attendeesHandler();
      this.$store.dispatch('modal/close', 'addAttendee');
    },
    AttendeesSelectorHandler() {
      if (this.attendeesArray.length < this.allMembers.length) {
        this.attendeesArray = this.allMembers.map(({ id }) => id);
      } else {
        this.attendeesArray = [];
      }
    },
    getSearchedText(text) {
      this.searchedMember = text;
      this.getAllMembersHandler(text);
    },
    async getAllMembersHandler(searchedMember) {
      this.isLoadingMembers = true;
      this.$store.dispatch('loading/skeletonLoader', {
        name: 'memberIsLoading',
        status: true,
      });
      axiosConfig
        .get(
          `/companies/${this.reserveMeetingCompanyId}/get-members?q=${
            searchedMember || ''
          }&columns[]=name`,
        )
        .then((response) => {
          const result = response.data.data;
          this.allMembers = result;
          this.isLoadingMembers = false;
          this.$store.dispatch('loading/skeletonLoader', {
            name: 'memberIsLoading',
            status: false,
          });
        })
        .catch((error) => {
          this.isLoadingMembers = false;
          this.$store.dispatch('loading/skeletonLoader', {
            name: 'memberIsLoading',
            status: false,
          });
          serverErrorsHandler(error);
          throw error;
        });
    },
  },
};
</script>

<style scoped>
.guest-section {
  @apply absolute overflow-auto bg-secondary shadow-lg rounded p-10 max-h-170;
  bottom: 50px;
  width: 98%;
}
.tooltip {
  position: absolute;
  bottom: 85%;
  left: 10%;
  transform: translateX(-50%);
  height: 0;
  width: 0;
  border: 0.5rem solid;
  border-color: var(--color-bg-secondary) transparent transparent;
  content: '';
  z-index: 2;
}
.disabled-div[disabled] {
  @apply border border-default;
  pointer-events: none;
}
.column-gap {
  gap: 10px;
}
.member-selector {
  @apply overflow-y-auto h-400;
}
.single-member {
  @apply flex items-center my-15;
}
.add-guest {
  @apply border border-primary h-32 w-32 rounded-sm flex
   items-center justify-center cursor-pointer disabled-div;
}
.guests-info {
  @apply border border-default rounded-sm w-full flex justify-between
   px-10 py-2 gap-x-20 items-center sm:flex-col sm:justify-center;
}
.guests-button {
  @apply w-170 h-40 bg-secondary border border-primary text-primary
   rounded flex items-center justify-center cursor-pointer;
}
.attendee-calendar-container {
  @apply w-1/2 sm:w-full;
}
</style>
<style>
.attendee-calendar-container .head-wrapper {
  @apply bg-primary !important;
}
.attendee-calendar-container .v-full-event-main-wrapper {
  @apply bg-primary !important;
}
.attendee-calendar-container .v-full-event-calender {
  padding: 0px !important;
}
</style>
